import React, { useEffect, useState } from "react";
import {
  Box, Typography, Stack, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress,
} from "@mui/material";
import { GroupAdd } from "@mui/icons-material";
import { referralsAPI, ReferralRecord } from "../../services/adminApi";

const AdminMarketingReferrals: React.FC = () => {
  const [records, setRecords] = useState<ReferralRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    referralsAPI.list({ limit: 500 })
      .then((res) => setRecords(res.data?.data ?? []))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#0D47A1 0%,#1565C0 55%,#1976D2 100%)",
        boxShadow: "0 8px 32px rgba(21,101,192,.3)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <GroupAdd sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                Referrals
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)", mt: 0.3 }}>
                Track who's inviting friends and who signed up through them
              </Typography>
            </Box>
          </Stack>
          <Chip label={`${records.length} referral${records.length === 1 ? "" : "s"}`} size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
        </Stack>
      </Box>

      <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
        <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Referral Log</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Most recent signups attributed to a referral link</Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#1565C0" }} />
          </Box>
        ) : records.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center", color: "#78909C" }}>
            <GroupAdd sx={{ fontSize: 48, color: "#BBDEFB", mb: 1 }} />
            <Typography variant="body2">No referrals recorded yet.</Typography>
            <Typography variant="caption">Once friends sign up via someone's invite link, they'll appear here.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Referrer</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Referred User</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Signed Up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: ".88rem", color: "#0A1628" }}>{r.referrerName}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C" }}>{r.referrerEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: ".88rem", color: "#0A1628" }}>{r.referredName}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C" }}>{r.referredEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#455A64" }}>
                        {new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminMarketingReferrals;
